import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, ArrowRightIcon, SparklesIcon } from 'lucide-react';
import { SpaDoors } from './SpaDoors';
export interface SpaRoom {
  id: string;
  name: string;
  tagline: string;
  blurb: string;
  description: string;
  features: string[];
  photos: string[];
  video?: string;
}
interface Props {
  room: SpaRoom | null;
  onClose: () => void;
}
export function RoomScene({
  room,
  onClose
}: Props) {
  const [doorsOpen, setDoorsOpen] = useState(false);
  const [active, setActive] = useState(0);
  // Auto-open the doors shortly after the room mounts
  useEffect(() => {
    if (!room) {
      setDoorsOpen(false);
      setActive(0);
      return;
    }
    const t = setTimeout(() => setDoorsOpen(true), 650);
    return () => clearTimeout(t);
  }, [room]);
  return <AnimatePresence data-id="element-179">
      {room && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.4
    }} className="fixed inset-0 z-[180] bg-aubergine-900" data-id="element-180">
          {room.video ? <div className="absolute inset-0 bg-black" data-id="element-181">
              <video src={room.video} autoPlay muted playsInline className="w-full h-full object-cover" data-id="element-182" />
              <div className="absolute inset-0 bg-gradient-to-t from-aubergine-900/90 via-transparent to-aubergine-900/50" data-id="element-183" />
            </div> : <SpaDoors open={doorsOpen} overlay={<div className="text-center px-6" data-id="element-185">
                  <div className="flex items-center justify-center gap-2 mb-3" data-id="element-186">
                    <SparklesIcon size={14} className="text-gold-500" data-id="element-187" />
                    <span className="text-[10px] uppercase tracking-luxe text-gold-500" data-id="element-188">
                      Down the Hall
                    </span>
                  </div>
                  <h2 className="font-serif text-4xl md:text-6xl text-aubergine-800" data-id="element-189">
                    {room.name}
                  </h2>
                  <p className="font-serif italic text-gold-500 mt-2" data-id="element-190">
                    Opening the doors…
                  </p>
                </div>} data-id="element-184">
              {/* Revealed real room */}
              <div className="absolute inset-0" data-id="element-191">
                <img src={room.photos[active]} alt={`${room.name} at Wonder of Life Wellness Spa`} className="w-full h-full object-cover" data-id="element-192" />
                <div className="absolute inset-0 bg-gradient-to-t from-aubergine-900/90 via-aubergine-900/30 to-aubergine-900/50" data-id="element-193" />

                {/* Soft candle glow + sparkle accents */}
                <motion.div animate={{
            opacity: [0.3, 0.55, 0.3]
          }} transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute left-[20%] top-[40%] w-40 h-40 rounded-full mix-blend-screen pointer-events-none" style={{
            background: 'radial-gradient(ellipse at center, rgba(255,220,150,0.4) 0%, transparent 70%)',
            filter: 'blur(16px)'
          }} data-id="element-194" />
              </div>
            </SpaDoors>}

          {/* Close */}
          <button onClick={onClose} aria-label="Leave the room" className="absolute top-5 right-5 z-40 w-11 h-11 rounded-full bg-aubergine-800/80 backdrop-blur border border-gold-400/30 flex items-center justify-center text-cream-50 hover:bg-aubergine-700 transition-colors" data-id="element-195">
            <XIcon size={18} data-id="element-196" />
          </button>

          {/* Room info panel */}
          <AnimatePresence data-id="element-197">
            {doorsOpen && <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.9,
          delay: room.video ? 2.5 : 0.5
        }} className="absolute inset-x-0 bottom-0 z-30 p-6 md:p-10" data-id="element-198">
                <div className="max-w-2xl mx-auto text-center" data-id="element-199">
                  <div className="flex items-center justify-center gap-2 mb-3" data-id="element-200">
                    <SparklesIcon size={13} className="text-gold-300" data-id="element-201" />
                    <span className="text-[10px] uppercase tracking-luxe text-gold-300" data-id="element-202">
                      {room.tagline}
                    </span>
                  </div>
                  <h2 className="font-serif text-4xl md:text-5xl text-cream-50 mb-3" data-id="element-203">
                    {room.name}
                  </h2>
                  <p className="font-serif italic text-gold-300/90 text-lg mb-4" data-id="element-204">
                    {room.blurb}
                  </p>
                  <p className="text-sm md:text-base text-cream-50/80 font-light leading-relaxed mb-6" data-id="element-205">
                    {room.description}
                  </p>

                  {/* Photo thumbnails */}
                  {!room.video && room.photos.length > 1 && <div className="flex items-center justify-center gap-3 mb-7" data-id="element-206">
                      {room.photos.map((p, i) => <button key={i} onClick={() => setActive(i)} aria-label={`View photo ${i + 1}`} className={`w-16 h-12 rounded-sm overflow-hidden border transition-all ${active === i ? 'border-gold-300 ring-2 ring-gold-300/40' : 'border-cream-50/20 opacity-70 hover:opacity-100'}`} data-id="element-207">
                          <img src={p} alt="" className="w-full h-full object-cover" data-id="element-208" />
                        </button>)}
                    </div>}

                  <a href="https://calendly.com/wonderoflifewellness1/30min" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 px-9 py-4 rounded-full bg-gold-400 text-aubergine-900 text-[11px] uppercase tracking-luxe hover:bg-gold-300 transition-colors duration-400" data-id="element-209">
                    Book This Room
                    <ArrowRightIcon size={14} className="group-hover:translate-x-1 transition-transform" data-id="element-210" />
                  </a>
                </div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>}
    </AnimatePresence>;
}